import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

/**
 * Coach mark de primera vez: tooltip que apunta a un elemento,
 * con flag persistido en localStorage.
 *
 * Se muestra una sola vez por `id`. Si el usuario lo descarta o tappea
 * el elemento referido, no vuelve a aparecer.
 *
 * Props:
 *  - id: identificador único (clave en localStorage).
 *  - title, body: copy.
 *  - position: 'bottom-right' | 'top-right' | 'top-center' | 'bottom-center'.
 *  - delay: ms antes de mostrar (default 800 — espera que el elemento monte).
 *  - enabled: si false no muestra (útil para condicionales por rol).
 */
const STORAGE_PREFIX = "altiv_coach_";

export default function CoachMark({
  id,
  title,
  body,
  position = "bottom-right",
  delay = 800,
  enabled = true,
}) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const key = STORAGE_PREFIX + id;
    try {
      if (localStorage.getItem(key) === "1") return;
    } catch {
      return;
    }
    const t = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(t);
  }, [id, delay, enabled]);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_PREFIX + id, "1");
    } catch {}
    setShow(false);
  };

  if (!show) return null;

  const posStyle =
    position === "bottom-right"
      ? { bottom: "calc(140px + env(safe-area-inset-bottom, 0px))", right: 12 }
      : position === "top-right"
        ? { top: "calc(env(safe-area-inset-top, 0px) + 56px)", right: 12 }
        : position === "top-center"
          ? {
              top: "calc(env(safe-area-inset-top, 0px) + 56px)",
              left: "50%",
              transform: "translateX(-50%)",
            }
          : {
              bottom: "calc(96px + env(safe-area-inset-bottom, 0px))",
              left: "50%",
              transform: "translateX(-50%)",
            };

  // top-right anchors to the "+" FAB en el header; con max-w-xs (320px)
  // el tooltip se extendía sobre el título de la página y lo recortaba a
  // "Centro" en pantallas de 390px. max-w-[208px] mantiene la flecha sobre
  // el "+" y libera el área del título.
  const maxWidthCls = position === "top-right" ? "max-w-[208px]" : "max-w-xs";

  return (
    <div
      className={`sm:hidden fixed z-[58] ${maxWidthCls} bg-[#0052FF] text-white rounded-xl shadow-2xl p-3 animate-fade-in`}
      style={posStyle}
      role="dialog"
      aria-labelledby={`coach-${id}-title`}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          {title && (
            <p id={`coach-${id}-title`} className="text-sm font-semibold leading-tight">
              {title}
            </p>
          )}
          {body && <p className="text-xs text-white/85 mt-1 leading-snug">{body}</p>}
        </div>
        <button
          onClick={dismiss}
          aria-label="Cerrar"
          className="w-7 h-7 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {/* Pointer arrow apuntando al FAB en bottom-right */}
      {position === "bottom-right" && (
        <span className="absolute -bottom-2 right-6 w-3 h-3 bg-[#0052FF] rotate-45" />
      )}
      {/* Pointer arrow apuntando hacia arriba al botón "+" del header */}
      {position === "top-right" && (
        <span className="absolute -top-1.5 right-4 w-3 h-3 bg-[#0052FF] rotate-45" />
      )}
    </div>
  );
}
